<template>
  <div class="news-detail-page">
    <Header />
    
    <!-- 页面标题区域 -->
    <section class="page-header">
      <div class="container">
        <div class="header-content">
          <div class="breadcrumb">
            <router-link to="/">首页</router-link>
            <span class="separator">/</span>
            <router-link to="/news">新闻资讯</router-link>
            <span class="separator">/</span>
            <span class="current">详情</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 新闻详情内容 -->
    <section class="news-detail-content">
      <div class="container">
        <div v-if="article" class="article-container">
          <!-- 文章头部 -->
          <div class="article-header">
            <div class="article-meta">
              <span class="category-tag">{{ article.category }}</span>
              <span class="publish-date">{{ formatDate(article.date) }}</span>
              <span class="views">{{ article.views }} 阅读</span>
            </div>
            <h1 class="article-title">{{ article.title }}</h1>
            <div class="article-image">
              <img :src="article.image" :alt="article.title" />
            </div>
          </div>

          <!-- 文章内容 -->
          <div class="article-body">
            <div class="article-excerpt">
              {{ article.excerpt }}
            </div>
            
            <div class="article-content">
              <div v-html="article.content"></div>
            </div>

            <!-- 文章标签 -->
            <div class="article-tags" v-if="article.tags && article.tags.length">
              <span class="tags-label">标签：</span>
              <span v-for="tag in article.tags" :key="tag" class="tag">{{ tag }}</span>
            </div>
          </div>

          <!-- 相关文章 -->
          <div class="related-articles" v-if="relatedArticles.length">
            <h3 class="related-title">相关文章</h3>
            <div :class="['related-grid', `related-count-${relatedArticles.length}`]">
              <div 
                v-for="related in relatedArticles" 
                :key="related.id"
                class="related-card"
                @click="viewRelatedArticle(related.id)"
              >
                <div class="related-image">
                  <img :src="related.image" :alt="related.title" />
                </div>
                <div class="related-content">
                  <h4 class="related-card-title">{{ related.title }}</h4>
                  <p class="related-excerpt">{{ related.excerpt }}</p>
                  <span class="related-date">{{ formatDate(related.date) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 文章不存在 -->
        <div v-else class="article-not-found">
          <div class="not-found-content">
            <h2>文章不存在</h2>
            <p>抱歉，您访问的文章不存在或已被删除。</p>
            <router-link to="/news" class="back-btn">返回新闻列表</router-link>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import { getNewsById, newsData } from '@/data/newsData'

export default {
  name: 'NewsDetail',
  components: {
    Header,
    Footer
  },
  data() {
    return {
      article: null,
      relatedArticles: []
    }
  },
  created() {
    this.loadArticle()
  },
  watch: {
    '$route'() {
      this.loadArticle()
    }
  },
  methods: {
    loadArticle() {
      const articleId = parseInt(this.$route.params.id)
      this.article = getNewsById(articleId)
      
      if (this.article) {
        // 增加阅读量
        this.article.views += 1
        
        // 加载相关文章（同分类的其他文章）
        this.loadRelatedArticles()
      }
    },
    loadRelatedArticles() {
      if (!this.article) return
      
      this.relatedArticles = newsData
        .filter(item => 
          item.id !== this.article.id && 
          item.categoryId === this.article.categoryId
        )
        .slice(0, 3) // 最多显示3篇相关文章
    },
    viewRelatedArticle(id) {
      this.$router.push(`/news/${id}`)
      // 滚动到页面顶部
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      })
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    }
  }
}
</script>

<style scoped>
.news-detail-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

/* 容器样式 */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

/* 页面标题区域 */
.page-header {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
  color: white;
  padding: 120px 0 60px;
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
  opacity: 0.3;
}

.header-content {
  position: relative;
  z-index: 2;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s ease;
}

.breadcrumb a:hover {
  color: white;
}

.separator {
  opacity: 0.6;
}

.current {
  color: white;
  font-weight: 500;
}

/* 新闻详情内容 */
.news-detail-content {
  padding: 4rem 0;
}

.article-container {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

/* 文章头部 */
.article-header {
  padding: 2rem;
  border-bottom: 1px solid #e5e7eb;
}

.article-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.category-tag {
  background: linear-gradient(135deg, #3b82f6, #1e40af);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.publish-date, .views {
  color: #6b7280;
  font-size: 0.875rem;
}

.article-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
  margin-bottom: 2rem;
}

.article-image {
  width: 100%;
  max-height: 500px;
  min-height: 250px;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
}

.article-image img {
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: contain;
  border-radius: 12px;
  transition: transform 0.3s ease;
}

.article-image img:hover {
  transform: scale(1.02);
}

/* 文章内容 */
.article-body {
  padding: 2rem;
}

.article-excerpt {
  font-size: 1.125rem;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border-left: 4px solid #3b82f6;
}

.article-content {
  color: #374151;
  line-height: 1.8;
  font-size: 1rem;
}

.article-content h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 2rem 0 1rem 0;
}

.article-content p {
  margin-bottom: 1rem;
}

.article-content ul {
  margin: 1rem 0;
  padding-left: 2rem;
}

.article-content li {
  margin-bottom: 0.5rem;
}

.article-content strong {
  color: #1f2937;
  font-weight: 600;
}

/* 文章标签 */
.article-tags {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.tags-label {
  color: #6b7280;
  font-weight: 500;
}

.tag {
  background: #f3f4f6;
  color: #374151;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.tag:hover {
  background: #e5e7eb;
}

/* 相关文章 */
.related-articles {
  margin-top: 3rem;
  padding: 2rem;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
}

.related-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1.5rem;
}

.related-grid {
  display: grid;
  gap: 1.5rem;
  margin: 0 auto;
}

/* 1个相关文章时的布局 */
.related-grid.related-count-1 {
  grid-template-columns: 1fr;
  max-width: 400px;
}

/* 2个相关文章时的布局 */
.related-grid.related-count-2 {
  grid-template-columns: repeat(2, 1fr);
  max-width: 800px;
}

/* 3个或更多相关文章时的布局 */
.related-grid.related-count-3,
.related-grid.related-count-4,
.related-grid.related-count-5 {
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  max-width: 1000px;
}

.related-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.related-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.related-image {
  height: 150px;
  overflow: hidden;
}

.related-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.related-card:hover .related-image img {
  transform: scale(1.1);
}

.related-content {
  padding: 1rem;
}

.related-card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

.related-excerpt {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.related-date {
  color: #9ca3af;
  font-size: 0.75rem;
}

/* 文章不存在 */
.article-not-found {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.not-found-content {
  text-align: center;
  background: white;
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.not-found-content h2 {
  font-size: 2rem;
  color: #1f2937;
  margin-bottom: 1rem;
}

.not-found-content p {
  color: #6b7280;
  margin-bottom: 2rem;
}

.back-btn {
  display: inline-block;
  background: linear-gradient(135deg, #3b82f6, #1e40af);
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.back-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container {
    padding: 0 1rem;
  }
  
  .article-title {
    font-size: 2rem;
  }
  
  .article-header, .article-body {
    padding: 1.5rem;
  }
  
  .article-meta {
    gap: 0.5rem;
  }
  
  .related-grid,
  .related-grid.related-count-1,
  .related-grid.related-count-2,
  .related-grid.related-count-3,
  .related-grid.related-count-4,
  .related-grid.related-count-5 {
    grid-template-columns: 1fr;
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .article-title {
    font-size: 1.75rem;
  }
  
  .article-header, .article-body {
    padding: 1rem;
  }
  
  .article-image {
    max-height: 300px;
    min-height: 200px;
  }
  
  .article-image img {
    max-height: 300px;
  }
}
</style>